'use client';

import { create } from 'zustand';
import type { AuthUser } from '@alqove/types';
import type { Membership } from '@alqove/api-client';
import { api } from '@/lib/api';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  /**
   * The user's active store memberships with resolved capabilities — the
   * ONE source the UI gates store features from (Port 00 §7). `null` means
   * not loaded (or the fetch failed); `[]` means loaded with none.
   */
  memberships: Membership[] | null;
  isLoading: boolean;

  setAuth: (user: AuthUser, token: string) => void;
  clearAuth: () => void;
  loadFromStorage: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

async function fetchMemberships(): Promise<Membership[] | null> {
  try {
    const response = await api.team.myMemberships();
    return response.data ?? [];
  } catch {
    // Auth must never depend on memberships loading; fail to "unknown".
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  memberships: null,
  isLoading: true,

  setAuth: (user, token) => {
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
  },

  clearAuth: () => {
    localStorage.removeItem('auth_token');
    set({ user: null, token: null, memberships: null, isLoading: false });
  },

  loadFromStorage: async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const [response, memberships] = await Promise.all([api.auth.me(), fetchMemberships()]);
      set({ user: response.data, token, memberships, isLoading: false });
    } catch {
      localStorage.removeItem('auth_token');
      set({ user: null, token: null, memberships: null, isLoading: false });
    }
  },

  refreshMemberships: async () => {
    set({ memberships: await fetchMemberships() });
  },

  login: async (email, password) => {
    const response = await api.auth.login({ email, password });
    const { user, token } = response.data;
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
    set({ memberships: await fetchMemberships() });
  },

  register: async (name, email, password) => {
    const response = await api.auth.register({
      name,
      email,
      password,
      password_confirmation: password,
    });
    const { user, token } = response.data;
    localStorage.setItem('auth_token', token);
    set({ user, token, isLoading: false });
    set({ memberships: await fetchMemberships() });
  },

  logout: async () => {
    try {
      await api.auth.logout();
    } catch {
      // Token may already be invalid — clear anyway
    }
    localStorage.removeItem('auth_token');
    set({ user: null, token: null, memberships: null, isLoading: false });
  },
}));

/** Selector: the caller's membership at a store, or undefined. */
export function selectMembershipFor(storeId: string | null | undefined) {
  return (state: AuthState): Membership | undefined =>
    storeId ? state.memberships?.find((m) => m.store_id === storeId) : undefined;
}
